import React from 'react';
import { graphql, useFragment } from 'react-relay';
import type { NavigationItemTab_tab$key } from '@atlassian/jira-relay/src/__generated__/NavigationItemTab_tab.graphql';
import type { NavigationItemTab_container$key } from '@atlassian/jira-relay/src/__generated__/NavigationItemTab_container.graphql';
import type { TabsDescriptor } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import { DefaultTab } from './DefaultTab.tsx';
import { AppTab } from './AppTab.tsx';
import { ShortcutsTab } from './shortcuts-tab/ShortcutsTab.tsx';

type Props = {
	tabFragment: NavigationItemTab_tab$key;
	containerFragment: NavigationItemTab_container$key;
	position: number;
	beforeItemId: string | null;
	afterItemId: string | null;
	tabsDescriptor: TabsDescriptor;
	isRenaming: boolean;
	onRenamingChange: (isRenaming: boolean) => void;
};

export const NavigationItemTab = ({
	tabFragment,
	containerFragment,
	position,
	beforeItemId,
	afterItemId,
	tabsDescriptor,
	isRenaming,
	onRenamingChange,
}: Props) => {
	const tabData = useFragment(
		graphql`
			fragment NavigationItemTab_tab on JiraNavigationItem {
				__typename
				typeKey @required(action: THROW)
				url
				...DefaultTab_tab
				...ShortcutsTab_tab
				...AppTab_tab
			}
		`,
		tabFragment,
	);
	const containerData = useFragment(
		graphql`
			fragment NavigationItemTab_container on JiraContainerNavigationResult {
				...DefaultTab_container
				...ShortcutsTab_container
				...AppTab_container
			}
		`,
		containerFragment,
	);

	if (tabData.__typename === 'JiraAppNavigationItem') {
		return (
			<AppTab
				tabFragment={tabData}
				containerFragment={containerData}
				position={position}
				beforeItemId={beforeItemId}
				afterItemId={afterItemId}
			/>
		);
	}

	if (tabData.typeKey === 'SHORTCUTS') {
		return (
			<ShortcutsTab
				tabFragment={tabData}
				containerFragment={containerData}
				position={position}
				beforeItemId={beforeItemId}
				afterItemId={afterItemId}
				isRenaming={isRenaming}
				onRenamingChange={onRenamingChange}
			/>
		);
	}

	if (tabData.url == null) {
		return null;
	}

	const Icon = tabsDescriptor[tabData.typeKey]?.Icon ?? null;

	return (
		<DefaultTab
			tabFragment={tabData}
			containerFragment={containerData}
			position={position}
			icon={Icon && <Icon label="" size="small" />}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
			isRenaming={isRenaming}
			onRenamingChange={onRenamingChange}
		/>
	);
};
